
import Vue from 'vue';
import { mapInformationManagerUsers } from '../utils/informationManager';
import {
  ERROR_MESSAGES,
  CYCLE_MESSAGES
} from '../utils/constants';

// Lazy accessor for Vue.axios to avoid race condition with plugin initialization
function getAxios() {
  return Vue.axios;
}

const BASE_URL = '/v2/financialclose/pbc/';

function initialState() {
  return {
    projectId: '',
    pbcprojectId: '',
    loading: false,
    uploadLoading: false,
    metadata: {},
    summary: {},
    users: [],
    error: null,
    requests: [],
    infoOwners: [],
    locations: [],
    filteredLocations: [],
    projectSummary: {},
    emailSummaryTemplate: { subject: '', body: '', placeholders: [] },
    emailTemplateLoaded: false,
    completeChecklist: [],
    completeChecklistLoaded: false,
  };
}

export default {
  namespaced: true,
  state: initialState(),
  getters: {
    projectId: state => state.projectId,
    isLoading: state => state.loading,
    isUploadLoading: state => state.uploadLoading,
    projectMetadata: state => state.metadata,
    summary: state => state.summary,
    userList: state => state.users,
    informationManagerError: state => state.error,
    requestList: state => state.requests,
    infoOwnerList: state => state.infoOwners,
    locationList: state => state.locations,
    filteredLocationList: state => state.filteredLocations,
    projectSummary: state => state.projectSummary,
    emailSummaryTemplate: state => state.emailSummaryTemplate,
    completeChecklist: state => state.completeChecklist,
  },
  mutations: {
    SET_PROJECT_ID(state, projectId) {
      state.projectId = projectId;
    },
    SET_LOADING(state, value) {
      state.loading = value;
    },
    SET_UPLOAD_LOADING(state, value) {
      state.uploadLoading = value;
    },
    SET_METADATA(state, metadata) {
      state.metadata = metadata;
      state.pbcprojectId = metadata.pbcproject;
    },
    SET_SUMMARY(state, summary) {
      state.summary = summary;
    },
    SET_USERS(state, users) {
      state.users = users;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    SET_REQUESTS(state, requests) {
      state.requests = requests;
    },
    SET_INFO_OWNERS(state, infoOwners) {
      state.infoOwners = infoOwners;
    },
    SET_LOCATIONS(state, locations) {
      state.locations = locations;
    },
    SET_FILTERED_LOCATIONS(state, locations) {
      state.filteredLocations = locations || [];
    },
    SET_PROJECT_SUMMARY(state, summary) {
      state.projectSummary = summary;
    },
    SET_EMAIL_SUMMARY_TEMPLATE(state, template) {
      state.emailSummaryTemplate = template || { subject: '', body: '', placeholders: [] };
      state.emailTemplateLoaded = true;
    },
    SET_COMPLETE_CHECKLIST(state, checklist) {
      state.completeChecklist = checklist || [];
      state.completeChecklistLoaded = true;
    },
    UPDATE_REQUEST_IN_LIST(state, updatedRequest) {
      const index = state.requests.findIndex(r => r._id === updatedRequest._id);
      if (index !== -1) {
        Vue.set(state.requests, index, updatedRequest);
      }
    },
    ADD_REQUEST_TO_LIST(state, newRequest) {
      state.requests.push(newRequest);
    },
    REMOVE_REQUEST_FROM_LIST(state, requestId) {
      state.requests = state.requests.filter(r => r._id !== requestId);
    },
    ADD_INFO_OWNER(state, infoOwner) {
      state.infoOwners.push(infoOwner);
    },
    UPDATE_INFO_OWNER(state, updatedOwner) {
      const index = state.infoOwners.findIndex(owner => owner._id === updatedOwner._id);
      if (index !== -1) {
        Vue.set(state.infoOwners, index, updatedOwner);
      }
    },
    RESET_STATE(state) {
      Object.assign(state, initialState());
    },
  },
  actions: {
    async bulkUploadPbc({ commit, dispatch, state }, { projectId, items }) {
      commit('SET_UPLOAD_LOADING', true);
      try {
        const response = await Vue.axios.post(`/v2/financialclose/analysis/bulkaddpbc/${projectId}`, { data: items });
        if (response.data.status === 'success') {
          commit('sbSuccess', ERROR_MESSAGES.INFORMATION_REQUIREMENTS_UPLOADED, { root: true });
          // If pbcprojectId is not set (PBC project was just created by this upload),
          // refresh project context to get the new pbcprojectId
          if (!state.pbcprojectId) {
            await dispatch('fetchProjectContext', { projectId: projectId || state.projectId });
          }
          return 'success';
        } else {
          throw new Error(response.data.message || ERROR_MESSAGES.ERROR_UPLOADING_INFORMATION_REQUIREMENTS);
        }
      } catch (err) {
        commit('sbError', err?.response?.data?.message || err?.message || err || 'Unknown error!', { root: true });
        return 'error';
      } finally {
        commit('SET_UPLOAD_LOADING', false);
      }
    },

    /**
     * V3 Page Load — Step 1+2 (parallel):
     * - /v3/financialclose/analysis/getdetails/:id (page: "im") → metadata
     * - /v3/financialclose/analysis/getformdata → users + locations (cached via tasks store)
     *
     * Replaces the old fetchProjectContext which called:
     * - /v2/financialclose/analysis/getdetails/:id (full payload with checklist, TB, pbcitems)
     * - /v2/financialclose/analysis/getformdata
     */
    async fetchProjectContext({ commit, dispatch }, { projectId } = {}) {
      if (!projectId) {
        const message = ERROR_MESSAGES.PROJECT_ID_REQUIRED;
        commit('SET_ERROR', message);
        commit('sbError', message, { root: true });
        return 'error';
      }
      commit('SET_PROJECT_ID', projectId);
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      try {
        // Parallel: v3/getdetails (slim metadata) + v3/getformdata (cached users+locations)
        const [detailsResponse, formData] = await Promise.all([
          getAxios().post(`/v3/financialclose/analysis/getdetails/${projectId}`),
          dispatch('tasks/fetchFormDataV3', {}, { root: true }),
        ]);

        // Handle detailsResponse (v3 returns flat data object, not data[0])
        const detailsStatus = detailsResponse?.data?.status;
        const detailsData = detailsResponse?.data?.data;
        if (detailsStatus !== 'success' || !detailsData) {
          throw new Error(detailsResponse?.data?.message || ERROR_MESSAGES.UNABLE_TO_LOAD_INFORMATION_MANAGER_DETAILS);
        }
        commit('SET_METADATA', detailsData.metadata || {});
        commit('SET_SUMMARY', detailsData.summary || {});
        commit('SET_FILTERED_LOCATIONS', detailsData.filteredLocations || []);

        // Handle formData (from tasks store cache)
        if (formData) {
          const nova = Vue.prototype?.$nova;
          const users = formData.users || [];
          const palette = nova?.getColors ? nova.getColors(users.length, 0.2) : [];
          const usersWithColors = mapInformationManagerUsers({ users }, palette);
          commit('SET_USERS', usersWithColors);
          commit('SET_LOCATIONS', formData.locations || []);
          if (formData.emailSummaryTemplate) {
            commit('SET_EMAIL_SUMMARY_TEMPLATE', formData.emailSummaryTemplate);
          }
        }

        return 'success';
      } catch (error) {
        const message = error?.message || 'Unable to refresh Information Manager data';
        commit('SET_ERROR', message);
        commit('sbError', message, { root: true });
        return 'error';
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * V3 Page Load — Step 3:
     * - /v3/pbc/project/getdata/:pbcProjectId → project summary + enriched requests
     * - /v2/pbc/infoowner/list → info owner dropdown
     *
     * Replaces the old fetchRequests which called 5 APIs:
     * - /v2/pbc/project/get/:id
     * - /v2/pbc/project/getrequests/:id
     * - /v2/pbc/infoowner/list
     * - /v2/financialclose/location/get (now from getformdata)
     * - /v2/pbc/project/getformdata (now lazy)
     */
    async fetchRequests({ commit, state }, { projectId } = {}) {
      const pbcProjectId = projectId || state.pbcprojectId;
      const cycleProjectId = state.projectId;
      commit('SET_LOADING', true);
      try {
        if (pbcProjectId) {
          // Parallel: v3 combined getdata + infoowners
          const [dataRes, infoOwnersRes] = await Promise.all([
            getAxios().post(`/v3/pbc/project/getdata/${pbcProjectId}`),
            getAxios().post(`${BASE_URL}infoowner/list`),
          ]);

          // 1. Combined project + requests from v3 endpoint
          if (dataRes.data.status === 'success' && dataRes.data.data) {
            const { project, requests } = dataRes.data.data;
            commit('SET_PROJECT_SUMMARY', project || {});
            commit('SET_REQUESTS', requests || []);
          } else {
            throw new Error(dataRes.data.message || ERROR_MESSAGES.NO_PROJECT_FOUND);
          }

          // 2. Info owners
          if (infoOwnersRes.data.status === 'success') {
            commit('SET_INFO_OWNERS', infoOwnersRes.data.data || []);
          } else {
            throw new Error(ERROR_MESSAGES.ERROR_FETCHING_INFORMATION_OWNER_LIST);
          }
        } else if (cycleProjectId) {
          // First-load fallback: legacy getrequests accepts the cycle project id
          // before a dedicated PBC project has been created.
          const [requestsRes, infoOwnersRes] = await Promise.all([
            getAxios().post(`${BASE_URL}project/getrequests/${cycleProjectId}`),
            getAxios().post(`${BASE_URL}infoowner/list`),
          ]);

          if (requestsRes.data.status === 'success') {
            commit('SET_PROJECT_SUMMARY', {});
            commit('SET_REQUESTS', requestsRes.data.data || []);
          } else {
            throw new Error(requestsRes.data.message || ERROR_MESSAGES.ERROR_FETCHING_DATA);
          }

          if (infoOwnersRes.data.status === 'success') {
            commit('SET_INFO_OWNERS', infoOwnersRes.data.data || []);
          } else {
            console.error('[informationManager]: Failed to fetch info owners', infoOwnersRes.data.message);
          }
        } else {
          // No PBC project — only fetch info owners
          const infoOwnersRes = await getAxios().post(`${BASE_URL}infoowner/list`);
          commit('SET_PROJECT_SUMMARY', {});
          commit('SET_REQUESTS', []);
          if (infoOwnersRes.data.status === 'success') {
            commit('SET_INFO_OWNERS', infoOwnersRes.data.data || []);
          } else {
            console.error('[informationManager]: Failed to fetch info owners', infoOwnersRes.data.message);
          }
        }

        return 'success';
      } catch (error) {
        const message = error.message || ERROR_MESSAGES.ERROR_FETCHING_DATA;
        commit('sbError', message, { root: true });
        return 'error';
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Lazy-load email summary template on first dialog open.
     * Replaces fetching /v2/pbc/project/getformdata on every page load.
     */
    async fetchEmailTemplate({ commit, state }) {
      if (state.emailTemplateLoaded) return state.emailSummaryTemplate;
      try {
        const res = await getAxios().post(`${BASE_URL}project/getformdata`);
        if (res.data.status === 'success') {
          const template = res.data.data?.[0]?.emailSummaryTemplate;
          if (template) {
            commit('SET_EMAIL_SUMMARY_TEMPLATE', template);
            return template;
          }
        }
        return state.emailSummaryTemplate;
      } catch (err) {
        console.error('[informationManager]: fetchEmailTemplate error', err);
        return state.emailSummaryTemplate;
      }
    },

    /**
     * Fetch a page of checklist tasks for the task picker in the request form.
     * Uses /v3/getchecklist/:id with pagination and optional text search.
     * Does NOT commit to store — returns data directly for component-local state.
     */
    async fetchChecklistPage(_, { projectId, pagination, search, searchFields, locationId }) {
      const body = {};
      if (pagination) body.pagination = pagination;
      if (search) {
        body.search = search;
        body.searchFields = searchFields || ['ref', 'description', 'locationName'];
      }
      if (locationId) {
        if (!body.filters) body.filters = {};
        body.filters.location = locationId;
      }
      const response = await getAxios().post(`/v3/financialclose/analysis/getchecklist/${projectId}`, body);
      if (response.data.status === 'success') {
        return response.data.data; // { records, totalCount, page, limit, totalPages }
      }
      throw new Error(response.data.message || 'Failed to fetch checklist page');
    },

    async fetchAllChecklist({ commit,state }, { projectId, locationId }) {
      // Return cached data only for unfiltered requests
      if (!locationId && state.completeChecklistLoaded && state.projectId === projectId) {
        return state.completeChecklist;
      }
      try {
        const body = {};
        if (locationId) {
          body.filters = { location: locationId };
        }
        const response = await getAxios().post(`/v3/financialclose/analysis/getchecklist/${projectId}`, body);
        if (response.data.status === 'success') {
          const checklist = response.data.data?.records || [];
          // Only cache the unfiltered result to avoid polluting the global cache
          // with a location-scoped subset
          if (!locationId) {
            commit('SET_COMPLETE_CHECKLIST', checklist);
          }
          return checklist;
        }
        throw new Error(response.data.message || 'Failed to fetch all checklist items');
      } catch (error) {
        console.error('Error fetching all checklist:', error);
        return [];
      }
    },

    async changeRequestStage({ commit, dispatch }, { requestId, stage }) {
      try {
        const res = await getAxios().post(`${BASE_URL}request/changestage/${requestId}/${stage}`);
        if (res.data.status === 'success') {
          commit('sbSuccess', CYCLE_MESSAGES.SUCCESS.STATUS_CHANGED || 'Status changed', { root: true });
          dispatch('fetchRequests'); // Refresh data
          return 'success';
        } else {
          throw new Error(res.data.message);
        }
      } catch (error) {
        commit('sbError', error.message || CYCLE_MESSAGES.ERROR.ERROR_CHANGING_STAGE || 'Error changing stage', { root: true });
        return 'error';
      }
    },
    async closeProject({ commit, dispatch }, { projectId }) {
      try {
        const res = await getAxios().post(`${BASE_URL}project/close/${projectId}`);
        if (res.data.status === 'success') {
          commit('sbSuccess', CYCLE_MESSAGES.SUCCESS.PROJECT_CLOSED || 'Project Closed Successfully', { root: true });
          dispatch('fetchRequests');
          return 'success';
        } else {
          throw new Error(res.data.message);
        }
      } catch (error) {
        commit('sbError', error.message || CYCLE_MESSAGES.ERROR.ERROR_CLOSING_PROJECT || 'Error closing project', { root: true });
        return 'error';
      }
    },
    async openProject({ commit, dispatch }, { projectId }) {
      try {
        const res = await getAxios().post(`${BASE_URL}project/open/${projectId}`);
        if (res.data.status === 'success') {
          commit('sbSuccess', CYCLE_MESSAGES.SUCCESS.PROJECT_REOPENED || 'Project re-opened Successfully', { root: true });
          dispatch('fetchRequests');
          return 'success';
        } else {
          throw new Error(res.data.message);
        }
      } catch (error) {
        commit('sbError', error.message || CYCLE_MESSAGES.ERROR.ERROR_REOPENING_PROJECT || 'Error opening project', { root: true });
        return 'error';
      }
    },
    async addRequest({ commit, dispatch, state }, { data }) {
      try {
        const res = await getAxios().post(`${BASE_URL}request/add/`, { data });
        if (res.data.status === 'success') {
          commit('sbSuccess', ERROR_MESSAGES.NEW_REQUEST_ADDED || 'New Request Added', { root: true });
          // If pbcprojectId is not set (PBC project was just created), refresh project context
          // to get the new pbcprojectId before fetching requests
          if (!state.pbcprojectId && state.projectId) {
            await dispatch('fetchProjectContext', { projectId: state.projectId });
          }
          dispatch('fetchRequests');
          return { status: 'success' };
        } else {
          return { status: 'error', data: res.data.data };
        }
      } catch (error) {
        commit('sbError', error?.response?.data?.message || error?.message || ERROR_MESSAGES.ERROR_ADDING_REQUEST || 'Error adding request', { root: true });
        return { status: 'error', message: error.message };
      }
    },
    async editRequest({ commit, dispatch }, { requestId, data }) {
      try {
        const res = await getAxios().post(`${BASE_URL}request/edit/${requestId}`, { data });
        if (res.data.status === 'success') {
          commit('sbSuccess', ERROR_MESSAGES.REQUEST_UPDATED || 'Request Updated', { root: true });
          dispatch('fetchRequests');
          return { status: 'success' };
        } else {
          return { status: 'error', data: res.data.data };
        }
      } catch (error) {
        commit('sbError', error.message || ERROR_MESSAGES.ERROR_EDITING_REQUEST || 'Error editing request', { root: true });
        return { status: 'error', message: error.message };
      }
    },
    async deleteRequest({ commit, dispatch }, { requestId, cycleId }) {
      try {
        const res = await getAxios().post(`${BASE_URL}request/delete/${requestId}`, { data: { cycleId: cycleId } });
        if (res.data.status === 'success') {
          commit('sbSuccess', ERROR_MESSAGES.REQUEST_DELETED || 'Request Deleted', { root: true });
          dispatch('fetchRequests');
          return 'success';
        } else {
          throw new Error(res.data.message || ERROR_MESSAGES.ERROR_DELETING_REQUEST || 'Error deleting request');
        }
      } catch (error) {
        commit('sbError', error.message || ERROR_MESSAGES.ERROR_DELETING_REQUEST || 'Error deleting request', { root: true });
        return 'error';
      }
    },
    async updateResponse({ commit, dispatch }, { requestId, data, files, client }) {
      try {
        const fileIds = [];
        if (Array.isArray(files) && files.length > 0) {
          const nova = Vue.prototype.$nova;
          const uploadResults = await Promise.all(files.filter(Boolean).map(async (file) => {
            const uploadRes = await nova.uploadFile(getAxios(), file);
            const items = uploadRes?.data?.data || [];
            if (uploadRes?.data?.status !== 'success' || items.length === 0) {
              throw new Error('Unable to upload file');
            }
            return items.map(item => item?._id).filter(Boolean);
          }));
          uploadResults.forEach(ids => fileIds.push(...ids));
        }

        const payload = { ...(data || {}) };
        if (payload.response === undefined || payload.response === null ||
          (typeof payload.response === 'string' && payload.response.trim() === '')) {
          payload.response = {};
        }
        if (fileIds.length > 0) {
          payload.fileid = fileIds;
        }

        const requestBody = { data: payload };
        if (client) {
          requestBody.client = client;
        }

        const res = await getAxios().post(`${BASE_URL}request/updateresponse/${requestId}`, requestBody);
        if (res.data.status === 'success') {
          commit('sbSuccess', 'Response Updated', { root: true });
          await dispatch('fetchRequests');
          return 'success';
        }

        const error = new Error(res.data.message || 'Error updating response');
        if (res.data.data) error.data = res.data.data;
        throw error;
      } catch (error) {
        console.error('[informationManager]: updateResponse error', error);
        commit('sbError', error.message || 'Error updating response', { root: true });
        throw error;
      }
    },
    async bulkUpdateInfoOwner({ commit, dispatch }, { projectId, data }) {
      try {
        const res = await getAxios().post(`/v2/financialclose/pbc/request/bulkupdateinfoowner/${projectId}`, { data });
        if (res.data.status === 'success') {
          commit('sbSuccess', 'The Information Owner Email Successfully Replaced', { root: true });
          dispatch('fetchRequests');
          return 'success';
        } else {
          throw new Error(res.data.message);
        }
      } catch (error) {
        commit('sbError', error.message || 'Error updating info owners', { root: true });
        return 'error';
      }
    },
    async addInfoOwner({ commit }, { data }) {
      try {
        const response = await getAxios().post(`${BASE_URL}infoowner/add/`, { data });
        if (response.data.status === 'success' && response.data.data.length > 0) {
          const newOwner = response.data.data[0];
          commit('ADD_INFO_OWNER', newOwner);
          commit('sbSuccess', 'New Information Owner Added', { root: true });
          return response.data;
        } else {
          return { status: 'error', data: response.data.data };
        }
      } catch (error) {
        commit('sbError', error?.response?.data?.message || error?.message || 'Error adding info owner', { root: true });
        return { status: 'error', message: error?.response?.data?.message || error?.message || 'Error adding info owner' };
      }
    },
    async editInfoOwner({ commit }, { id, data }) {
      try {
        const res = await getAxios().post(`${BASE_URL}infoowner/edit/${id}`, { data });
        if (res.data.status === 'success' && res.data.data.length > 0) {
          const updatedOwner = res.data.data[0];
          commit('UPDATE_INFO_OWNER', updatedOwner);
          commit('sbSuccess', 'Updated Owner Information', { root: true });
          return res.data;
        } else {
          return { status: 'error', data: res.data.data };
        }
      } catch (error) {
        commit('sbError', error.message || 'Error editing info owner', { root: true });
        return { status: 'error', message: error.message };
      }
    },
    async addComment({ commit }, { projectId, data }) {
      try {
        const res = await getAxios().post(`${BASE_URL}request/addcomment/${projectId}`, { data });
        if (res.data.status === 'success' && res.data.data.length > 0) {
          commit('sbSuccess', 'Comment Added', { root: true });
          return { status: 'success', data: res.data.data[0] };
        } else {
          return { status: 'error', data: res.data.data };
        }
      } catch (error) {
        commit('sbError', error.message || 'Error adding comment', { root: true });
        return { status: 'error', message: error.message };
      }
    },
    async markCommentAsRead({ commit }, { requestId }) {
      try {
        const res = await getAxios().post(`${BASE_URL}request/markcommentasread/${requestId}`);
        if (res.data.status === 'success') {
          return 'success';
        } else {
          throw new Error(res.data.message);
        }
      } catch (error) {
        commit('sbError', error.message || 'Error marking comment as read', { root: true });
        return 'error';
      }
    },
    async sendMail({ commit, dispatch }, { projectId, data }) {
      try {
        const res = await getAxios().post(`${BASE_URL}request/sendmail/${projectId}`, { data });
        if (res.data.status === 'success') {
          commit('sbSuccess', 'Mail sent', { root: true });
          dispatch('fetchRequests');
          return 'success';
        } else {
          throw new Error(res.data.message || 'Error sending mail');
        }
      } catch (error) {
        commit('sbError', error.message || 'Error sending mail', { root: true });
        return 'error';
      }
    },
    async fetchEmailSuggestions({ state }, { projectId } = {}) {
      try {
        const pid = projectId || state.pbcprojectId;
        if (!pid) return [];
        const response = await getAxios().post(`/v2/pbc/project/getemailsuggestions/${pid}`);
        if (response.data.status === 'success') {
          return response.data.data?.[0]?.suggestions || [];
        }
        return [];
      } catch (error) {
        console.error('[informationManager]: fetchEmailSuggestions error', error);
        return [];
      }
    },
    async triggerEmailSummary({ commit }, { projectId, selectedItems, to, cc, body, subject }) {
      try {
        const response = await getAxios().post(`/v2/pbc/project/triggeremailsummary`, {
          data: [{
            project: projectId,
            pbc_requests: selectedItems,
            to_emails: to,
            cc_emails: cc || [],
            body: body,
            subject: subject,
          }],
        });
        if (response.data.status === 'success') {
          commit('sbSuccess', 'Email summary has been initiated successfully', { root: true });
          return 'success';
        } else {
          throw new Error(response.data.message || 'Failed to trigger email summary');
        }
      } catch (error) {
        console.error('[StoreAction]: triggerEmailSummary error', error);
        const rawMessage = error?.response?.data?.message || error?.message;
        const message = (typeof rawMessage === 'string') ? rawMessage : 'Failed to trigger email summary';
        commit('sbError', message, { root: true });
        return 'error';
      }
    },
    async uploadBulkRequirements({ state, commit, dispatch }, { projectId, items } = {}) {
      const targetProjectId = projectId || state.projectId;
      if (!targetProjectId) {
        const message = 'Project id is required to upload requirements';
        commit('sbError', message, { root: true });
        return 'error';
      }
      if (!Array.isArray(items) || items.length === 0) {
        const message = 'No Information Manager requirements provided for upload';
        commit('sbError', message, { root: true });
        return 'error';
      }

      commit('SET_UPLOAD_LOADING', true);
      try {
        const response = await getAxios().post(`/v2/financialclose/analysis/bulkaddpbc/${targetProjectId}`, { data: items });
        if (response?.data?.status !== 'success') {
          throw new Error(response?.data?.message || 'Error uploading Information Manager requirements');
        }
        commit('sbSuccess', 'Information Requirements uploaded successfully', { root: true });
        await dispatch('fetchProjectContext', { projectId: targetProjectId });
        return 'success';
      } catch (error) {
        const message = error?.response?.data?.message ||error?.message || 'Unknown error uploading Information Manager requirements';
        commit('SET_ERROR', message);
        commit('sbError', message, { root: true });
        return 'error';
      } finally {
        commit('SET_UPLOAD_LOADING', false);
      }
    },
    resetState({ commit }) {
      commit('RESET_STATE');
      return 'success';
    },
  },
};
